<template>
  <Section :settings="sectionSettings">
    <div class="hero-section" :class="alignmentClass">
      <div class="d-flex content-container">
        <div class="content d-flex flex-column">
          <div class="logo" v-if="editable || companyLogo">
            <ImageUploader
              height="60px"
              width="230px"
              layout="horizontal"
              objectFit="contain"
              selectFileText="Add your logo here"
              :alignment="logoAlignment"
              :editable="editable"
              :image="companyLogo"
              :imageStyles="{ maxWidth: '300px' }"
              :uploadBoxStyles="{ backgroundColor: '#ffffff' }"
              @fileAdded="onImageChange($event, 'company_logo')"
              @fileRemoved="onImageChange(null, 'company_logo')"
            />
          </div>
          <div class="text">
            <UeInlineEditable
              tag="h1"
              class="mt-0 mb-4"
              :editable="editable"
              :value="settings.title"
              @editable_field:save="handleTitleChange"
            />
            <UeInlineEditable
              class="description"
              :editable="editable"
              :value="settings.description"
              @editable_field:save="handleDescriptionChange"
            />
          </div>
          <div class="cta mt-5">
            <CtaButton v-if="settings.show_cta" :settings="settings" />
          </div>
        </div>
        <div
          class="image d-flex align-items-end justify-content-end text-right"
          :class="{ 'pb-3 pr-3': showImageShadow }"
          v-if="showImage"
        >
          <ImageUploader
            height="95%"
            selectFileText="Upload an image"
            width="95%"
            :editable="editable"
            :image="image"
            :uploadBoxStyles="imageUploadBoxStyles"
            @fileAdded="onImageChange($event, 'image')"
            @fileRemoved="onImageChange(null, 'image')"
          />
        </div>
      </div>
    </div>
  </Section>
</template>
<script lang="ts" setup>
import { defineProps, computed, CSSProperties } from 'vue'
import { DeleteImagePayload, HeroContent, HeroSectionSettings, UploadImagePayload } from './types'
import { Section as SectionInterface } from './types'
import Section from './Section.vue'
import CtaButton from './CtaButton.vue'
import ImageUploader from '@components/customer_page/ImageUploader.vue'
import UeInlineEditable from '@components/base/UeInlineEditable.vue'
import { getColorById } from './utils'

interface Props {
  content: HeroContent
  editable?: boolean
  sectionId: string
  settings?: HeroSectionSettings
}

const props = withDefaults(defineProps<Props>(), {
  editable: false,
  content: () => ({
    company_logo: null,
    image: null,
  }),
  settings: () =>
    ({
      title: 'Hero Section',
      show_title: true,
      alignment: 'left',
      cta: 'Learn more',
      cta_url: '#',
      show_cta: true,
      show_img_shadow: true,
      description: 'Hero Section Description',
      show_description: true,
      design_settings: {
        colors: [],
      },
    }) as HeroSectionSettings,
})

const emit = defineEmits<{
  (e: 'imageAdded', payload: UploadImagePayload): void
  (e: 'imageDeleted', payload: DeleteImagePayload): void
  (e: 'content-change', payload: Partial<SectionInterface>): void
}>()

const sectionSettings = computed(() => {
  return {
    ...props.settings,
    show_title: false,
  }
})

const image = computed(() => props.content?.image?.blob_url)
const companyLogo = computed(() => props.content?.company_logo?.blob_url)
const logoAlignment = computed(() =>
  // We need the logo aligned to the left even on right alignment
  sectionSettings.value.alignment === 'center' ? 'center' : 'left',
)
const showImage = computed(() => {
  return alignmentClass.value !== 'center-aligned' && (props.editable || image.value)
})

const contentWidth = computed(() => (props.editable || image.value ? '60%' : 'auto'))
const alignmentClass = computed(() => {
  switch (props.settings.alignment) {
    case 'center':
      return 'center-aligned'
    case 'right':
      return 'right-aligned'
    default:
      return 'left-aligned'
  }
})

const imgShadowColor = computed(() => {
  return getColorById(props.settings.design_settings?.colors, 'image_shadow_color') || '#000000'
})

const showImageShadow = computed(() => {
  return props.settings.show_img_shadow && showImage
})

const imageUploadBoxStyles = computed<CSSProperties>(() => {
  const styles: CSSProperties = {
    aspectRatio: '1/1',
    backgroundColor: '#ffffff',
    transition: 'box-shadow 0.2s ease-in-out',
  }

  if (showImageShadow.value) {
    styles.boxShadow = `16px 16px 0px ${imgShadowColor.value}`
  }

  return styles
})

const handleTitleChange = (text: string): void => {
  emit('content-change', {
    id: props.sectionId,
    settings: { ...props.settings, title: text },
    content: props.content,
  })
}

const handleDescriptionChange = (text: string): void => {
  emit('content-change', {
    id: props.sectionId,
    settings: { ...props.settings, description: text },
    content: props.content,
  })
}

const onImageChange = (file: File | null, image_attribute: string): void => {
  const payload = { image_attribute, section_id: props.sectionId }

  if (file) {
    emit('imageAdded', { ...payload, file })
  } else {
    const fileId =
      image_attribute === 'image' ? props.content?.image?.id : props.content?.company_logo?.id
    emit('imageDeleted', { ...payload, id: fileId })
  }
}
</script>

<style scoped lang="sass">
.content-container
  // height: 370px
  gap: 20px

.hero-section
  .content
    .text
      flex: 1 1 auto

    .cta
      flex: 0 0 auto

    .logo
      flex: 0 1 auto
      margin-bottom: 3rem
      min-height: 80px
      max-height: 80px

  &.left-aligned, &.right-aligned
    .content
      flex: 0 1 v-bind(contentWidth)

    .image
      flex: 0 0 40%

  &.center-aligned
    .content
      width: 100%
      text-align: center
      align-items: center

      .text
        width: 75%

  &.right-aligned
    .content-container
      gap: 40px
    .content
      order: 2
      align-items: start
</style>
